class BundleBuilder {
  constructor(container) {
    this.container = container;
    this.openButton = container.querySelector("[data-bundle-open]");
    this.modal = container.querySelector("[data-bundle-modal]");
    this.closeButtons = container.querySelectorAll("[data-bundle-close]");
    this.modalId = container.dataset.bundleModalId;
    this.currentProductId = String(container.dataset.currentProductId || "");
    this.submitButton = container.querySelector("[data-bundle-submit]");
    this.errorNode = container.querySelector("[data-bundle-error]");
    this.minItems = Number(container.dataset.minItems || 1);
    this.maxItems = Number(container.dataset.maxItems || 99);
    if (this.maxItems < this.minItems) this.maxItems = this.minItems;
    this.bundleTitle = container.dataset.bundleTitle || "Bundle";
    this.countdownEndsAt = container.dataset.bundleCountdownEnd || "";
    this.countdownRoot = container.querySelector("[data-bundle-countdown]");
    this.countdownTimer = null;
    this.cart =
      document.querySelector("cart-notification") ||
      document.querySelector("cart-drawer");

    this.initializeCountdown();

    if (!this.submitButton) return;
    this.moveModalToBody();
    this.scope = this.modal || this.container;
    this.moveCurrentProductToTop();

    if (this.openButton && this.modal) {
      this.openButton.addEventListener("click", () => this.openModal());
      this.closeButtons.forEach((button) => {
        button.addEventListener("click", () => this.closeModal());
      });
      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && !this.modal.hidden) {
          this.closeModal();
        }
      });
    }

    this.initializeSelectionState();
    this.scope
      .querySelectorAll(
        ".bundle-builder__check, .bundle-builder__qty, .bundle-builder__variant",
      )
      .forEach((field) => {
        field.addEventListener("change", (event) =>
          this.syncState(event.target),
        );
        field.addEventListener("input", (event) =>
          this.syncState(event.target),
        );
      });

    this.syncState();
    this.submitButton.addEventListener("click", () => this.handleSubmit());
  }

  initializeCountdown() {
    if (!this.countdownRoot || !this.countdownEndsAt) return;

    const parsedEnd = new Date(this.countdownEndsAt);
    if (Number.isNaN(parsedEnd.getTime())) return;

    this.countdownDaysNode =
      this.countdownRoot.querySelector("[data-bundle-countdown-days]");
    this.countdownHoursNode =
      this.countdownRoot.querySelector("[data-bundle-countdown-hours]");
    this.countdownMinutesNode =
      this.countdownRoot.querySelector("[data-bundle-countdown-minutes]");
    this.countdownSecondsNode =
      this.countdownRoot.querySelector("[data-bundle-countdown-seconds]");

    this.updateCountdown(parsedEnd);
    this.countdownTimer = window.setInterval(
      () => this.updateCountdown(parsedEnd),
      1000,
    );
  }

  updateCountdown(endDate) {
    const remainingMs = endDate.getTime() - Date.now();

    if (remainingMs <= 0) {
      if (this.countdownDaysNode) this.countdownDaysNode.textContent = "00";
      if (this.countdownHoursNode) this.countdownHoursNode.textContent = "00";
      if (this.countdownMinutesNode)
        this.countdownMinutesNode.textContent = "00";
      if (this.countdownSecondsNode)
        this.countdownSecondsNode.textContent = "00";
      if (this.countdownTimer) {
        window.clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
      return;
    }

    const totalSeconds = Math.floor(remainingMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (this.countdownDaysNode)
      this.countdownDaysNode.textContent = String(days).padStart(2, "0");
    if (this.countdownHoursNode)
      this.countdownHoursNode.textContent = String(hours).padStart(2, "0");
    if (this.countdownMinutesNode)
      this.countdownMinutesNode.textContent = String(minutes).padStart(2, "0");
    if (this.countdownSecondsNode)
      this.countdownSecondsNode.textContent = String(seconds).padStart(2, "0");
  }

  initializeSelectionState() {
    this.scope.querySelectorAll(".bundle-builder__item").forEach((itemRow) => {
      const checked = itemRow.querySelector(".bundle-builder__check");
      const qtyField = itemRow.querySelector(".bundle-builder__qty");
      const isCurrentProduct =
        this.currentProductId &&
        itemRow.dataset.bundleProductId === this.currentProductId;
      if (checked) checked.checked = Boolean(isCurrentProduct);
      if (qtyField) qtyField.value = "1";
    });
  }

  moveCurrentProductToTop() {
    if (!this.currentProductId) return;
    const itemsContainer = this.scope.querySelector(".bundle-builder__items");
    if (!itemsContainer) return;

    const currentItem = itemsContainer.querySelector(
      `.bundle-builder__item[data-bundle-product-id="${this.currentProductId}"]`,
    );
    if (!currentItem) return;

    itemsContainer.prepend(currentItem);
  }

  openModal() {
    if (!this.modal) return;
    this.modal.hidden = false;
    if (this.openButton) this.openButton.setAttribute("aria-expanded", "true");
    document.body.classList.add("bundle-builder-modal-open");

    // Always reset modal to default state on open:
    // current PDP product selected, others unselected.
    this.moveCurrentProductToTop();
    this.initializeSelectionState();
    this.syncState();

    const itemsContainer = this.scope.querySelector(".bundle-builder__items");
    if (itemsContainer) {
      itemsContainer.scrollTop = 0;
      // Force reflow to stabilize fixed modal layout on mobile select interactions.
      void itemsContainer.offsetHeight;
    }
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.hidden = true;
    if (this.openButton) this.openButton.setAttribute("aria-expanded", "false");
    document.body.classList.remove("bundle-builder-modal-open");
  }

  moveModalToBody() {
    if (!this.modal || this.modal.dataset.bundleTeleported === "true") return;
    if (this.modalId) this.modal.id = this.modalId;
    document.body.appendChild(this.modal);
    this.modal.dataset.bundleTeleported = "true";
  }

  getSelectedItems() {
    const items = [];

    this.scope.querySelectorAll(".bundle-builder__item").forEach((itemRow) => {
      const checked = itemRow.querySelector(".bundle-builder__check");
      if (!checked || !checked.checked) return;

      const variantField = itemRow.querySelector(".bundle-builder__variant");
      const qtyField = itemRow.querySelector(".bundle-builder__qty");

      const variantId = Number(variantField?.value);
      const quantity = Number(qtyField?.value || 0);

      if (!variantId || quantity < 1) return;

      items.push({
        id: variantId,
        quantity,
        properties: {
          Bundle: this.bundleTitle,
        },
      });
    });

    return items;
  }

  getItemPrice(itemRow) {
    const variantField = itemRow.querySelector(".bundle-builder__variant");
    if (!variantField) return 0;
    if (variantField.tagName === "SELECT") {
      const option = variantField.options[variantField.selectedIndex];
      return Number(option?.dataset.price || 0);
    }
    return Number(variantField.dataset.price || 0);
  }

  formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    if (!Number.isFinite(amount)) return "Rs 0.00";
    try {
      return `Rs ${amount.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    } catch (e) {
      return `Rs ${amount.toFixed(2)}`;
    }
  }

  updateTotals() {
    this.scope.querySelectorAll(".bundle-builder__item").forEach((itemRow) => {
      const itemPriceNode = itemRow.querySelector("[data-item-price]");
      const unitPrice = this.getItemPrice(itemRow);
      if (itemPriceNode)
        itemPriceNode.textContent = this.formatMoney(unitPrice);
    });
  }

  getSelectedCount() {
    let selectedCount = 0;
    this.scope.querySelectorAll(".bundle-builder__item").forEach((itemRow) => {
      const checked = itemRow.querySelector(".bundle-builder__check");
      if (!checked?.checked) return;
      const qtyField = itemRow.querySelector(".bundle-builder__qty");
      selectedCount += Math.max(0, Number(qtyField?.value || 0));
    });
    return selectedCount;
  }

  updateDisabledState() {
    const selectedCount = this.getSelectedCount();
    const atMax = selectedCount >= this.maxItems;

    this.scope.querySelectorAll(".bundle-builder__item").forEach((itemRow) => {
      const checked = itemRow.querySelector(".bundle-builder__check");
      const variantField = itemRow.querySelector(".bundle-builder__variant");
      const qtyField = itemRow.querySelector(".bundle-builder__qty");
      if (!checked) return;

      const isChecked = checked.checked;
      const disableUnselected = atMax && !isChecked;

      checked.disabled = disableUnselected;
      if (variantField) variantField.disabled = disableUnselected;
      if (qtyField) qtyField.disabled = !isChecked;
      itemRow.classList.toggle("is-disabled", disableUnselected);
      itemRow.classList.toggle("is-unselected", !isChecked);
    });
  }

  clampOverMax(changedField) {
    const selectedCount = this.getSelectedCount();
    if (selectedCount <= this.maxItems || !changedField) return;

    if (
      changedField.classList.contains("bundle-builder__check") &&
      changedField.checked
    ) {
      changedField.checked = false;
      return;
    }

    if (changedField.classList.contains("bundle-builder__qty")) {
      const itemRow = changedField.closest(".bundle-builder__item");
      const checked = itemRow?.querySelector(".bundle-builder__check");
      if (!checked?.checked) return;

      const overflow = selectedCount - this.maxItems;
      const currentQty = Math.max(1, Number(changedField.value || 1));
      changedField.value = String(Math.max(1, currentQty - overflow));
    }
  }

  syncState(changedField = null) {
    this.scope.querySelectorAll(".bundle-builder__qty").forEach((qtyField) => {
      const raw = Number(qtyField.value || 0);
      if (!Number.isFinite(raw) || raw < 1) qtyField.value = "1";
    });

    this.clampOverMax(changedField);
    this.updateDisabledState();
    this.updateTotals();
    this.setError("");
  }

  setError(message = "") {
    if (!this.errorNode) return;
    this.errorNode.textContent = message;
    this.errorNode.hidden = !message;
  }

  setLoading(isLoading) {
    this.submitButton.disabled = isLoading;
    this.submitButton.classList.toggle("loading", isLoading);
  }

  async handleSubmit() {
    this.setError("");

    const items = this.getSelectedItems();
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    if (itemCount < this.minItems || itemCount > this.maxItems) {
      this.setError(
        `Please select between ${this.minItems} and ${this.maxItems} items.`,
      );
      return;
    }

    const payload = { items };

    if (this.cart) {
      payload.sections = this.cart
        .getSectionsToRender()
        .map((section) => section.id);
      payload.sections_url = window.location.pathname;
      this.cart.setActiveElement(document.activeElement);
    }

    const config = fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    config.body = JSON.stringify(payload);

    this.setLoading(true);

    try {
      const response = await fetch(`${routes.cart_add_url}`, config);
      const data = await response.json();

      if (data.status) {
        this.setError(data.description || "Unable to add bundle to cart.");
        publish(PUB_SUB_EVENTS.cartError, {
          source: "bundle-builder",
          errors: data.errors || data.description,
          message: data.message,
        });
        return;
      }

      if (!this.cart) {
        window.location = window.routes.cart_url;
        return;
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "bundle-builder",
        cartData: data,
      });

      this.cart.renderContents(data);
      this.closeModal();
    } catch (error) {
      this.setError("Something went wrong. Please try again.");
      console.error(error);
    } finally {
      this.setLoading(false);
      if (this.cart && this.cart.classList.contains("is-empty")) {
        this.cart.classList.remove("is-empty");
      }
    }
  }
}

class DiscountInfoCountdown {
  constructor(root) {
    this.root = root;
    this.countdownEndsAt = root.dataset.countdownEnd || "";
    this.daysNode = root.querySelector("[data-discount-countdown-days]");
    this.hoursNode = root.querySelector("[data-discount-countdown-hours]");
    this.minutesNode = root.querySelector("[data-discount-countdown-minutes]");
    this.secondsNode = root.querySelector("[data-discount-countdown-seconds]");
    this.timer = null;

    this.initialize();
  }

  initialize() {
    if (!this.countdownEndsAt) return;

    const parsedEnd = new Date(this.countdownEndsAt);
    if (Number.isNaN(parsedEnd.getTime())) return;

    this.update(parsedEnd);
    this.timer = window.setInterval(() => this.update(parsedEnd), 1000);
  }

  update(endDate) {
    const remainingMs = endDate.getTime() - Date.now();

    if (remainingMs <= 0) {
      if (this.daysNode) this.daysNode.textContent = "00";
      if (this.hoursNode) this.hoursNode.textContent = "00";
      if (this.minutesNode) this.minutesNode.textContent = "00";
      if (this.secondsNode) this.secondsNode.textContent = "00";
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
      return;
    }

    const totalSeconds = Math.floor(remainingMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (this.daysNode) this.daysNode.textContent = String(days).padStart(2, "0");
    if (this.hoursNode) this.hoursNode.textContent = String(hours).padStart(2, "0");
    if (this.minutesNode) this.minutesNode.textContent = String(minutes).padStart(2, "0");
    if (this.secondsNode) this.secondsNode.textContent = String(seconds).padStart(2, "0");
  }
}

class DiscountCodeCopy {
  constructor(button) {
    this.button = button;
    this.discountCode = button.dataset.discountCode || "";
    this.defaultLabel = button.textContent.trim() || "Copy";

    button.addEventListener("click", () => this.copy());
  }

  async copy() {
    if (!this.discountCode) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(this.discountCode);
      } else {
        const input = document.createElement("input");
        input.value = this.discountCode;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        input.remove();
      }

      this.button.textContent = "Copied";
      window.setTimeout(() => {
        this.button.textContent = this.defaultLabel;
      }, 1800);
    } catch (error) {
      this.button.textContent = "Copy";
    }
  }
}

function initializeBundleBuilders() {
  document.querySelectorAll("[data-bundle-builder]").forEach((container) => {
    if (container.dataset.bundleBuilderReady === "true") return;
    container.dataset.bundleBuilderReady = "true";
    new BundleBuilder(container);
  });

  document.querySelectorAll("[data-discount-info-countdown]").forEach((root) => {
    if (root.dataset.discountCountdownReady === "true") return;
    root.dataset.discountCountdownReady = "true";
    new DiscountInfoCountdown(root);
  });

  document.querySelectorAll("[data-discount-copy-button]").forEach((button) => {
    if (button.dataset.discountCopyReady === "true") return;
    button.dataset.discountCopyReady = "true";
    new DiscountCodeCopy(button);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeBundleBuilders);
} else {
  initializeBundleBuilders();
}

document.addEventListener("shopify:section:load", initializeBundleBuilders);
